import { useEffect, useState } from "react";
import api, { fileUrl, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, FileText, Plane, UserCircle2, FolderArchive, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BookingMessagesPanel from "@/components/messaging/BookingMessagesPanel";
import ProfileCard from "@/components/profile/ProfileCard";
import ChangePasswordCard from "@/components/profile/ChangePasswordCard";
import VendorTwoFactorPanel from "@/components/vendor/VendorTwoFactorPanel";
import CustomerDocumentsCard from "@/components/profile/CustomerDocumentsCard";
import LicenseExpiryBanner from "@/components/profile/LicenseExpiryBanner";
import RequestChangesMenu from "@/components/customer/RequestChangesMenu";
import BookingLogisticsCard from "@/components/booking/BookingLogisticsCard";
import BookingPayHostCard from "@/components/booking/BookingPayHostCard";
import BookingCodeChip from "@/components/booking/BookingCodeChip";
import BookingExtrasPanel from "@/components/bookings/BookingExtrasPanel";
import LoyaltyTab from "@/components/loyalty/LoyaltyTab";
import WhatsAppButton from "@/components/whatsapp/WhatsAppButton";
import DirectionsLink from "@/components/booking/DirectionsLink";
import VendorNameLink from "@/components/common/VendorNameLink";
import ReviewPromptCard from "@/components/booking/ReviewPromptCard";
import CustomerCancelDialog from "@/components/bookings/CustomerCancelDialog";
import InvoiceDeliveryActions from "@/components/invoices/InvoiceDeliveryActions";
import Money from "@/components/Money";
import { NextRentalHero, CustomerBottomNav, TripStatusChips, tripBucket, VehicleChips, CustomerCardActions, PaymentBreakdown, BookingTimeline, TripReminderBanner } from "@/components/customer/MobileDashboard";

const FALLBACK = "https://images.unsplash.com/photo-1576933765009-9f57f43910aa?auto=format&fit=crop&w=400&q=70";

// Normalise the car's first image to a fully-qualified URL or fall back to a stock photo.
// Extracted to avoid a deep nested ternary in the JSX cell.
function _carThumbnailSrc(car) {
  const first = car?.images?.[0];
  if (!first) return FALLBACK;
  return first.startsWith("http") ? first : fileUrl(first);
}

// Pick the right deposit-status badge based on the operator's decision. Pulled out
// of the JSX so the nested-ternary block on the customer dashboard reads top-to-bottom.
function _depositStatusNode(b) {
  if (b.deposit_status === "released") {
    return <span className="text-emerald-700 font-medium">Refunded</span>;
  }
  if (b.deposit_status !== "withheld") {
    return <span className="text-amber-700 font-medium">Held</span>;
  }
  const fullyWithheld = Number(b.deposit_withheld_amount ?? b.deposit).toFixed(2) === Number(b.deposit).toFixed(2);
  return (
    <span className="text-rose-700 font-medium">
      {fullyWithheld ? "Withheld" : <><Money amount={b.deposit_withheld_amount} /> withheld</>}
      {Number(b.deposit_refund_amount || 0) > 0 && (
        <span className="text-emerald-700"> · <Money amount={b.deposit_refund_amount} /> refunded</span>
      )}
      {b.deposit_decision_reason ? <span className="text-neutral-500 font-normal"> — {b.deposit_decision_reason}</span> : null}
    </span>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  // Iter 92 — controlled tabs (bottom nav) + trip bucket filter.
  const [activeTab, setActiveTab] = useState("trips");
  const [tripFilter, setTripFilter] = useState("all");
  const [invoicesByBooking, setInvoicesByBooking] = useState({});
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [unreadByBooking, setUnreadByBooking] = useState({});

  const load = () => api.get("/bookings/mine").then((r) => setBookings(r.data));
  // Iter 67bd — show the invoice link inline on the booking row (next to the
  // Agreement link). We pull the customer's issued/adjusted/settled invoices
  // once and key them by booking_id so each row can render its own invoice
  // affordance without an extra round-trip.
  const loadInvoices = async () => {
    try {
      const { data } = await api.get("/customer/invoices?limit=200");
      const map = {};
      for (const inv of data.items || []) {
        if (inv.booking_id) map[inv.booking_id] = inv;
      }
      setInvoicesByBooking(map);
    } catch {
      /* silent — invoices are optional on a booking */
    }
  };
  const loadUnread = async () => {
    try {
      const { data } = await api.get("/messages/unread-count");
      setUnreadByBooking(data.by_booking || {});
    } catch (e) {
      console.error("unread count load failed:", e);
    }
  };
  useEffect(() => { load(); loadUnread(); loadInvoices(); }, []);

  // Iter 74e — auto-submit review when landing here via a "Rate your host"
  // email link (?review_booking_id=X&rating=N). We wait until `bookings` are
  // loaded so we can look up car_id, then POST the review, then clean the
  // URL so a refresh doesn't re-submit.
  useEffect(() => {
    const url = new URL(window.location.href);
    const bid = url.searchParams.get("review_booking_id");
    const rParam = parseInt(url.searchParams.get("rating") || "0", 10);
    if (!bid || rParam < 1 || rParam > 5 || bookings.length === 0) return;
    const booking = bookings.find((b) => b.id === bid);
    if (!booking) return;
    // Idempotency: if already reviewed, just clean the URL.
    if (booking.has_review) {
      url.searchParams.delete("review_booking_id");
      url.searchParams.delete("rating");
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
      return;
    }
    (async () => {
      try {
        await api.post("/reviews", {
          car_id: booking.car_id,
          booking_id: booking.id,
          rating: rParam,
          comment: "",
        });
        toast.success(
          rParam >= 4
            ? "Thanks for the review — your host will love it!"
            : "Thanks — your review helps future guests."
        );
        setBookings((prev) => prev.map((row) =>
          row.id === bid ? { ...row, has_review: true, my_rating: rParam } : row
        ));
      } catch (e) {
        // Silent if "Already reviewed" — the flag will refresh on next load.
        if (!/already reviewed/i.test(String(e?.response?.data?.detail || ""))) {
          toast.error(formatApiError(e));
        }
      } finally {
        url.searchParams.delete("review_booking_id");
        url.searchParams.delete("rating");
        window.history.replaceState({}, "", url.pathname + (url.search || ""));
      }
    })();
  }, [bookings]);

  // Background-poll unread-count every 30s so the badge updates even when no chat is open.
  useEffect(() => {
    const id = setInterval(loadUnread, 30000);
    return () => clearInterval(id);
  }, []);

  const submitReview = async () => {
    try {
      await api.post("/reviews", {
        car_id: reviewing.car_id,
        booking_id: reviewing.id,
        rating, comment,
      });
      toast.success("Review submitted");
      // Iter 74c — hide the prompt for this booking without full reload.
      const bookingId = reviewing.id;
      const submittedRating = rating;
      setBookings((prev) => prev.map((row) =>
        row.id === bookingId
          ? { ...row, has_review: true, my_rating: submittedRating }
          : row
      ));
      setOpen(false); setComment(""); setRating(5);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user) return null;

  return (
    <div>
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-display text-3xl lg:text-4xl tracking-tight font-medium mb-2">{t("dashboard.myBookings")}</h1>
        <p className="text-sm text-neutral-500 mb-8">{t("dashboard.welcomeBack", { name: user.name })}</p>

        <LicenseExpiryBanner licenseExpiry={user.license_expiry} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start flex-wrap gap-2 h-auto bg-gradient-to-r from-sky-50/70 via-blue-50/40 to-indigo-50/60 border border-sky-100 p-2 rounded-2xl mb-8 shadow-sm ring-1 ring-sky-50" data-testid="customer-main-tabs">
            <TabsTrigger
              value="trips"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-sky-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-sky-200 hover:text-sky-700 transition-all"
              data-testid="customer-tab-trips"
            >
              <span className="h-7 w-7 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center ring-1 ring-sky-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <Plane className="h-4 w-4" />
              </span>
              My trips
              {bookings.length > 0 && (
                <span className="ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-white/25 text-current font-bold ring-1 ring-white/40 data-[state=inactive]:bg-sky-100 data-[state=inactive]:text-sky-700">
                  {bookings.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="profile"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-200 hover:text-blue-700 transition-all"
              data-testid="customer-tab-profile"
            >
              <span className="h-7 w-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center ring-1 ring-blue-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <UserCircle2 className="h-4 w-4" />
              </span>
              Profile
            </TabsTrigger>
            <TabsTrigger
              value="rewards"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-amber-200 hover:text-amber-700 transition-all"
              data-testid="customer-tab-rewards"
            >
              <span className="h-7 w-7 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center ring-1 ring-amber-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <Sparkles className="h-4 w-4" />
              </span>
              Rewards
            </TabsTrigger>
            <TabsTrigger
              value="documents"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-violet-200 hover:text-violet-700 transition-all"
              data-testid="customer-tab-documents"
            >
              <span className="h-7 w-7 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center ring-1 ring-violet-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <FolderArchive className="h-4 w-4" />
              </span>
              Documents
            </TabsTrigger>
            <TabsTrigger
              value="security"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-emerald-200 hover:text-emerald-700 transition-all"
              data-testid="customer-tab-security"
            >
              <span className="h-7 w-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <ShieldCheck className="h-4 w-4" />
              </span>
              Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trips" className="mt-0" data-testid="customer-content-trips">
            {/* Iter 92 — mobile-first: next-rental hero + status buckets. */}
            <TripReminderBanner bookings={bookings} />
            <NextRentalHero bookings={bookings} />
            {bookings.length > 0 && (
              <TripStatusChips
                counts={bookings.reduce((acc, b) => {
                  acc.all += 1;
                  acc[tripBucket(b)] = (acc[tripBucket(b)] || 0) + 1;
                  return acc;
                }, { all: 0, upcoming: 0, active: 0, past: 0, cancelled: 0 })}
                active={tripFilter}
                onChange={setTripFilter}
              />
            )}
            {bookings.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border p-16 text-center">
                <div className="text-neutral-700 font-medium mb-1">{t("dashboard.noTrips")}</div>
                <div className="text-sm text-neutral-500">{t("dashboard.noTripsHelp")}</div>
              </div>
            ) : (
              <div className="space-y-3">
            {bookings.filter((b) => tripFilter === "all" || tripBucket(b) === tripFilter).map((b) => {
              const unread = unreadByBooking[b.id] || 0;
              // Iter 65: 3-sided coloured frame — confirmed=green, pending_payment=amber,
              // pending=sky, cancelled=rose, completed=slate. Matches the vendor dashboard.
              const status = (b.status || "").toLowerCase();
              const frameClass =
                status === "confirmed"
                  ? "border-y-2 border-l-[6px] border-emerald-500"
                  : status === "pending_payment"
                  ? "border-y-2 border-l-[6px] border-amber-500"
                  : status === "cancelled"
                  ? "border-y-2 border-l-[6px] border-rose-500"
                  : status === "completed"
                  ? "border-y-2 border-l-[6px] border-slate-400"
                  : "border-y-2 border-l-[6px] border-sky-500";
              return (
              <div
                key={b.id}
                className={`rounded-2xl border-r border-border bg-white p-5 ${frameClass}`}
                data-testid={`booking-${b.id}`}
                data-booking-status={status}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                  <div className="h-40 w-full sm:h-20 sm:w-28 rounded-xl bg-neutral-100 overflow-hidden flex-shrink-0">
                    <img src={_carThumbnailSrc(b.car)} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Iter 65: clickable booking code chip — click to copy to clipboard. */}
                    <div className="mb-1.5">
                      <BookingCodeChip bookingId={b.id} />
                    </div>
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      {b.car?.make} {b.car?.model}
                      <VehicleChips car={b.car} />
                      {unread > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-semibold" data-testid={`unread-badge-${b.id}`}>
                          {unread}
                        </span>
                      )}
                    </div>
                    {b.car?.vendor && (
                      <div className="text-xs text-neutral-500 mt-0.5" data-testid={`booking-vendor-${b.id}`}>
                        Hosted by{" "}
                        <VendorNameLink
                          vendor={b.car.vendor}
                          className="text-blue-600 hover:underline font-medium"
                          testIdSuffix={`customer-booking-${b.id}`}
                        />
                      </div>
                    )}
                    <div className="text-sm text-neutral-500">{b.start_date?.slice(0,10)} → {b.end_date?.slice(0,10)} · {b.days} days</div>
                    {/* Iter 93 — paid vs remaining amounts on the card. */}
                    <PaymentBreakdown booking={b} />
                    {/* Iter 100 — Stripe balance link sent by the host. */}
                    {b.balance_payment?.url && b.balance_payment?.status === "pending" && (
                      <a
                        href={b.balance_payment.url}
                        className="inline-flex items-center justify-center rounded-xl bg-blue-600 text-white text-xs font-bold px-3 py-2 mt-2 hover:bg-blue-700 transition"
                        data-testid={`pay-balance-card-${b.id}`}
                      >
                        Pay remaining balance online
                      </a>
                    )}
                    {b.pickup_location && (
                      <div className="text-xs text-neutral-500 mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`booking-route-${b.id}`}>
                        <span>
                          Pickup: <span className="text-neutral-700 font-medium">{b.pickup_location}</span> → Drop-off: <span className="text-neutral-700 font-medium">{b.dropoff_location}</span>
                        </span>
                        <DirectionsLink location={b.pickup_location} size="xs" testId={`booking-pickup-directions-${b.id}`} />
                      </div>
                    )}
                    {b.deposit > 0 && (
                      <div className="text-xs mt-1" data-testid={`customer-deposit-${b.id}`}>
                        Deposit <Money amount={b.deposit} /> · {_depositStatusNode(b)}
                        {b.deposit_status === "withheld" && Array.isArray(b.deposit_evidence_paths) && b.deposit_evidence_paths.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {b.deposit_evidence_paths.map((p) => (
                              <a
                                key={p}
                                href={p.startsWith("http") ? p : fileUrl(p)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`customer-evidence-${b.id}`}
                              >
                                <img src={p.startsWith("http") ? p : fileUrl(p)} alt="evidence" className="h-10 w-10 rounded-md object-cover border border-border" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <Badge variant={b.status === "confirmed" ? "default" : "secondary"}>{b.status}</Badge>
                    <Money amount={b.amount} className="text-sm font-semibold mt-1 block" />
                    <Link to={`/agreements/${b.id}`} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1.5" data-testid={`customer-booking-agreement-${b.id}`}>
                      <FileText className="h-3 w-3" /> Agreement
                    </Link>
                    {/* Iter 87 — booking invoice PDF in English or Albanian. */}
                    <div className="mt-1 flex items-center justify-end gap-2">
                      <a
                        href={`${API}/bookings/${b.id}/invoice.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                        data-testid={`customer-invoice-en-${b.id}`}
                      >
                        <FileText className="h-3 w-3" /> Invoice EN
                      </a>
                      <a
                        href={`${API}/bookings/${b.id}/invoice.pdf?lang=sq`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
                        data-testid={`customer-invoice-sq-${b.id}`}
                      >
                        <FileText className="h-3 w-3" /> Fatura SQ
                      </a>
                    </div>
                    {invoicesByBooking[b.id] && (
                      <div className="mt-1.5 flex items-center justify-end gap-2 flex-wrap" data-testid={`customer-booking-invoice-${b.id}`}>
                        <span className="inline-flex items-center gap-1 text-xs text-indigo-700 font-medium">
                          <FileText className="h-3 w-3" /> Invoice
                          <span className="text-neutral-500 font-normal">· {invoicesByBooking[b.id].invoice_number}</span>
                        </span>
                        <InvoiceDeliveryActions
                          kind="rental"
                          invoiceId={invoicesByBooking[b.id].id}
                          invoiceNumber={invoicesByBooking[b.id].invoice_number}
                          variant="icons"
                        />
                      </div>
                    )}
                    {(b.status === "confirmed" || b.status === "completed") && (
                      <ReviewPromptCard
                        booking={b}
                        onReviewed={(bookingId, rating) => {
                          // Optimistically flip the flag so the prompt hides
                          // without needing a full reload.
                          setBookings((prev) => prev.map((row) =>
                            row.id === bookingId
                              ? { ...row, has_review: true, my_rating: rating }
                              : row
                          ));
                        }}
                        onOpenDialog={(bk, preset) => {
                          setReviewing(bk);
                          setRating(preset || 5);
                          setOpen(true);
                        }}
                      />
                    )}
                    {/* Iter 67ay — renter self-cancellation. Hidden once the
                        booking is already cancelled, completed, or refunded so
                        the dashboard doesn't show stale destructive buttons. */}
                    {!["cancelled", "completed", "refunded"].includes(b.status) && (
                      <CustomerCancelDialog booking={b} onCancelled={load} />
                    )}
                    <div className="mt-1.5"><RequestChangesMenu booking={b} onChanged={load} /></div>
                    {b.car?.vendor?.whatsapp && (
                      <div className="mt-1.5 flex justify-end">
                        <WhatsAppButton
                          phone={b.car.vendor.whatsapp}
                          message={`Hi! I'm ${user?.name || "your RentalCar.al customer"} for booking #${b.id?.slice(0, 8).toUpperCase()} (${b.car?.make} ${b.car?.model}, ${b.start_date?.slice(0,10)} → ${b.end_date?.slice(0,10)}).`}
                          label="Message host"
                          testId={`customer-whatsapp-${b.id}`}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {/* Iter 93 — Booked → Confirmed → Pickup → Returned progress. */}
                <div className="mt-3"><BookingTimeline booking={b} /></div>
                {/* Iter 93 — large touch-friendly actions + quick contacts. */}
                <CustomerCardActions booking={b} />
                <BookingLogisticsCard booking={b} vendorId={b.vendor_id} onSaved={load} />
                <BookingExtrasPanel booking={b} />
                {b.status === "confirmed" && Number(b.commission_amount || 0) >= 0 && (
                  <BookingPayHostCard booking={b} />
                )}
                <BookingMessagesPanel
                  bookingId={b.id}
                  counterpartyName={b.car?.vendor?.name || "the host"}
                  onUnreadChange={loadUnread}
                />
              </div>
              );
            })}
          </div>
            )}
          </TabsContent>

          <TabsContent value="profile" className="mt-0" data-testid="customer-content-profile">
            <ProfileCard />
          </TabsContent>

          <TabsContent value="rewards" className="mt-0" data-testid="customer-content-rewards">
            <LoyaltyTab />
          </TabsContent>

          <TabsContent value="documents" className="mt-0" data-testid="customer-content-documents">
            <div className="space-y-4">
              <CustomerDocumentsCard />
            </div>
          </TabsContent>

          <TabsContent value="security" className="mt-0 space-y-6" data-testid="customer-content-security">
            <ChangePasswordCard />
            {/* Iter 69 — same 2FA + trusted-devices panel used by vendors,
                opened up to customers too. The endpoint paths are
                `/api/vendor/2fa/...` for backward compat but the auth
                guard is now "any logged-in user". */}
            <VendorTwoFactorPanel />
          </TabsContent>
        </Tabs>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Leave a review</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Rating</Label>
                <div className="flex gap-1 mt-2">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} type="button" onClick={() => setRating(n)} data-testid={`rating-${n}`}>
                      <Star className={`h-7 w-7 ${n <= rating ? "fill-yellow-400 text-yellow-400" : "text-neutral-300"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Comment</Label>
                <Textarea className="mt-1 rounded-xl" rows={4} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
              </div>
              <Button onClick={submitReview} className="rounded-full w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-review-btn">Submit review</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <CustomerBottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className="h-16 md:hidden" />
      <Footer />
    </div>
  );
}
